/**
 * ChartTooltip Component
 *
 * Custom tooltip for the Sales Timeline charts.
 * Shows detailed breakdown of demand and supply by strain.
 *
 * US-PE-006: Sales Timeline - Phase 2 Chart Components
 */

import { getDayLabelFull, formatChartDate } from './chart-config'

/**
 * Custom tooltip for Recharts ComposedChart
 *
 * @param {Object} props - Recharts tooltip props
 * @param {boolean} props.active - Whether tooltip is active
 * @param {Array} props.payload - Data payload from Recharts
 * @param {string} props.label - X-axis label (day name)
 * @param {Array} [props.strains] - Array of strain objects with id, name, color
 * @param {boolean} [props.showAllStrains] - Whether showing all strains (stacked view)
 */
export default function ChartTooltip({
  active,
  payload,
  label,
  strains = [],
  showAllStrains = false
}) {
  if (!active || !payload || payload.length === 0) {
    return null
  }

  // Extract data from payload
  const dataPoint = payload[0]?.payload
  if (!dataPoint) {
    return null
  }

  // Get the date for display
  const date = dataPoint.date
  const dayLabel = date ? getDayLabelFull(date) : label
  const formattedDate = date ? formatChartDate(date) : ''

  // Extract demand value
  const demandEntry = payload.find(p => p.dataKey === 'demand')
  const demandKg = demandEntry?.value ?? dataPoint.demand ?? 0

  // Calculate total supply from bars
  let totalSupplyKg = 0
  const supplyBreakdown = []

  if (showAllStrains && strains.length > 0) {
    // Build supply breakdown from strain-specific bar data
    strains.forEach(strain => {
      const strainKey = `supply_${strain.id}`
      const strainEntry = payload.find(p => p.dataKey === strainKey)
      const strainValue = strainEntry?.value ?? dataPoint[strainKey] ?? 0

      if (strainValue > 0) {
        supplyBreakdown.push({
          name: strain.name,
          color: strain.color,
          value: strainValue
        })
        totalSupplyKg += strainValue
      }
    })
  } else {
    // Single strain view - use total supply
    const supplyEntry = payload.find(p => p.dataKey === 'supply')
    totalSupplyKg = supplyEntry?.value ?? dataPoint.supply ?? dataPoint.totalSupplyKg ?? 0
  }

  // Calculate gap
  const gapKg = totalSupplyKg - demandKg
  const hasShortfall = gapKg < 0

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 min-w-[180px] z-50"
      style={{ backgroundColor: 'white', position: 'relative' }}
      role="tooltip"
    >
      {/* Date header */}
      <div className="border-b border-slate-100 pb-2 mb-2">
        <div className="font-medium text-slate-900">
          {dayLabel}
        </div>
        {formattedDate && (
          <div className="text-xs text-slate-500">
            {formattedDate}
          </div>
        )}
      </div>

      {/* Demand */}
      <div className="flex items-center justify-between py-1">
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-0.5 rounded"
            style={{ backgroundColor: '#1e293b' }}
            aria-hidden="true"
          />
          <span className="text-sm text-slate-600">Demand</span>
        </div>
        <span className="text-sm font-medium text-slate-900">
          {formatWeight(demandKg)}
        </span>
      </div>

      {/* Supply breakdown (if multiple strains) */}
      {showAllStrains && supplyBreakdown.length > 0 && (
        <div className="border-t border-slate-100 pt-2 mt-2">
          <div className="text-xs text-slate-500 mb-1">Supply by strain</div>
          {supplyBreakdown.map((item, index) => {
            // Get batch breakdown for this strain
            const batches = dataPoint.batchesByStrain?.[item.name] || []
            const showBatches = batches.length > 0

            return (
              <div key={index} className="py-0.5">
                {/* Strain row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-sm"
                      style={{ backgroundColor: item.color }}
                      aria-hidden="true"
                    />
                    <span className="text-sm text-slate-600">{item.name}</span>
                  </div>
                  <span className="text-sm text-slate-700">
                    {formatWeight(item.value)}
                  </span>
                </div>
                {/* Batch breakdown (indented under strain) */}
                {showBatches && (
                  <div className="ml-5 mt-0.5">
                    {batches.map((batch, batchIndex) => (
                      <div
                        key={batchIndex}
                        className="flex items-center justify-between text-xs text-slate-500"
                      >
                        <span className="font-mono">{batch.code}</span>
                        <span>{formatWeight(batch.kg)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Total supply */}
      <div className="flex items-center justify-between py-1 border-t border-slate-100 mt-2 pt-2">
        <span className="text-sm font-medium text-slate-700">Total Supply</span>
        <span className="text-sm font-medium text-slate-900">
          {formatWeight(totalSupplyKg)}
        </span>
      </div>

      {/* Gap indicator */}
      {demandKg > 0 && (
        <div
          className={`flex items-center justify-between py-1.5 px-2 rounded mt-2 ${
            hasShortfall
              ? 'bg-red-50 text-red-700'
              : gapKg > 0
                ? 'bg-green-50 text-green-700'
                : 'bg-slate-50 text-slate-600'
          }`}
        >
          <span className="text-xs font-medium">
            {hasShortfall ? 'Shortfall' : gapKg > 0 ? 'Surplus' : 'Balanced'}
          </span>
          <span className="text-xs font-semibold">
            {hasShortfall ? '' : '+'}{formatWeight(gapKg)}
          </span>
        </div>
      )}
    </div>
  )
}

/**
 * Format weight value for display
 *
 * @param {number} kg - Weight in kilograms
 * @returns {string} Formatted weight with unit
 */
function formatWeight(kg) {
  if (kg === 0) return '0 kg'

  // Show one decimal place if needed
  const formatted = Math.abs(kg) < 10
    ? kg.toFixed(1).replace(/\.0$/, '')
    : Math.round(kg).toString()

  return `${formatted} kg`
}
